import React, { useContext, useRef, useState } from "react"
import PQueue from 'p-queue';
import { isEqual } from "lodash"

import useLog from "../hooks/useLog"
import { translateErrorAsString, translateErrorToJSXMessage } from "../translateErrorToJSXMessage"
import RunContext from "./RunContext";
import { getLog } from "../helpers/helpers";


const bsv = require('bsv') // make sure you load it from externals or it could be incompatible with Run


/* basic status for sendtx
	this can be fully overriden to make more complex status, by passing a 'specific_state' to functions
	that sets {"status": ... }
*/
export const sendtx_status = {
	empty: 0,
	sending: 1,
	sentsuccess: 2,
	sentfail: 3,
}

const TxStateContext = React.createContext(null)

const queue = new PQueue({ concurrency: 1 })



export const TxStateContextProvider = (props) => {

	const function_id = "TxStateContext(): "
	const classname = function_id
	const log = useLog(function_id)

	// global broadcast lock
	const [broadcast_lock, set_broadcast_lock] = useState(false)

	// object of 1 token-type => 1 tx state, should be fine until we have dozens++ of token types
	const [sendtxs_state, DONTCALL_set_sendtxs_state] = useState({}) // set with setTxState(tokentype, new_token_state)

	const runContext = useContext(RunContext)

	const ref_sendtxs_state = useRef()
	ref_sendtxs_state.current = sendtxs_state  // call ref_sendtxs_state.current to have the latest value in setTimeout() or similar calls

	const state = {
		sendtxs_state: sendtxs_state,
		broadcast_lock: broadcast_lock,

		// general function to update a sendTx state
		setTxState: setTxState,

		// helper functions
		init_action: init_action,
		sendtx_deal_with_success: sendtx_deal_with_success,
		deal_with_error_RETURN: deal_with_error_RETURN,

		// BEWARE ONCE YOU TAKE THE LOCK DONT RETURN or throw WITHOUT RELEASING IT
		take_broadcast_lock: take_broadcast_lock,
		release_broadcast_lock: release_broadcast_lock,
	}

	log("sendtxs_state=", sendtxs_state,
		"\n broadcast_lock=", broadcast_lock)

	async function take_broadcast_lock() {
		// BEWARE ONCE YOU TAKE THE LOCK DONT RETURN or throw WITHOUT RELEASING IT
		set_broadcast_lock(true)
		// if not already done, retrieve run in this 
		if (!this.myrun) {
			this.myrun = await runContext.get_run_asyncf()
		}
	}
	async function release_broadcast_lock() {
		// BEWARE ONCE YOU TAKE THE LOCK DONT RETURN or throw WITHOUT RELEASING IT
		set_broadcast_lock(false)
	}

	/**
	 * no need to await when calling
	 * 
	 * we use a queue to make sure state is updated sequentially
	 * otherwise we could lose some state info with 2 parallel calls to set_sendtxs_state()
	 * 
	 * because useState() keeps the state at the call, we need to use a Ref (ref_sendtxs_state.current)
	 * to make sure the call will always have the latest state
	 */
	async function setTxState(tokenActionID, state_to_update) {
		const function_id = classname + "setTxState(): "
		const log = getLog(function_id)
		log("called with tokenActionID=", tokenActionID, "state_to_update=", state_to_update)
		queue.add(async () => {
			// only place we can call this
			DONTCALL_setTxState_inner(tokenActionID, state_to_update)
		})
		log("queue.size=", queue.size)
	}


	/**
	 * we enforce updating the full state (for a tokentype) at once to avoid inconsistent states
	 * for example if we updated just the timestamp for a new tx and we would still
	 * have status in sentfail
	 * 
	 * because we called from a queue, we need a ref to make sure we have the latest updated value
	 * just like if the call was from setTimeout()
	 */
	async function DONTCALL_setTxState_inner(tokenActionID, state_to_update) {
		const function_id = classname + "_setTxState_inner(): "
		const log = getLog(function_id)

		// because we called from a queue, we need a ref to make sure we have the latest updated value
		// just like if the call was from setTimeout()
		var last_sendtxs_state = ref_sendtxs_state.current

		var tokenActionID_state_old = {}
		var tokenActionID_state_new = {}

		if (last_sendtxs_state && tokenActionID in last_sendtxs_state) {
			tokenActionID_state_old = last_sendtxs_state[tokenActionID]
		}

		tokenActionID_state_new = {
			...tokenActionID_state_old,
			...state_to_update,
		}

		log("\n tokenActionID=", tokenActionID,
			"\n tokenActionID_state_old=", JSON.stringify(tokenActionID_state_old),
			"\n tokenActionID_state_new=", JSON.stringify(tokenActionID_state_new))

		if (isEqual(tokenActionID_state_old, tokenActionID_state_new)) {
			log("aborting: already correct state when asking to update tokenActionID=", tokenActionID,
				"from", JSON.stringify(tokenActionID_state_old), "to", JSON.stringify(tokenActionID_state_new)
			)
			return
		}

		DONTCALL_set_sendtxs_state({ // only place we can call this
			...last_sendtxs_state,
			[tokenActionID]: tokenActionID_state_new,
		})
	}


	/**
	 * ----------------- // shouldn't be necessary with the Ref, 30.12.2021
	 * beware you must await
	 * beware you must retrieve the currtx_state returned to remain synced
	 * ----------------- // shouldn't be necessary with the Ref, 30.12.2021
	 */
	async function init_action(
		function_id, tokenActionID, specific_state = {}
	) {
		const log = getLog(function_id)

		if (broadcast_lock) { // safety check
			log("broadcast_lock already taken, aborting.")
			return
		}

		// BEWARE ONCE YOU TAKE THE LOCK DONT RETURN or throw WITHOUT RELEASING IT
		set_broadcast_lock(true)

		// timestamp can be used as an uuid to identify that tx easily from outside context
		var tx_state = {
			"timestamp": Date.now(),
			"status": sendtx_status.sending,
			...specific_state,
		}

		setTxState(tokenActionID, tx_state)

		// return tx_state // shouldn't be necessary with the Ref, 30.12.2021
	}



	/** you need to always return this function !
	 * you must return when you call this function to make sure you exit
	 * 
	 * callback_func example: ()=>{userContext.syncWallet_nForceRerender()}
	 */
	function deal_with_error_RETURN(
		// function_id, tokenActionID, currtx_state, error, prefix_message="", specific_state={}
		// // currtx_state shouldn't be necessary with the Ref, 30.12.2021
		function_id, tokenActionID, error, prefix_message = "", specific_state = {}, callback_func
	) {
		function_id = "deal_with_error_RETURN(): " + function_id
		console.error(function_id + prefix_message, "error: ", error)

		var parsed_error = translateErrorAsString(error, prefix_message)

		// trying to return this in the currtx_state as it handles more stuff, 30.12.2021
		// answer: not good it doesn't display the message but the generic error with "more info" button
		// var parsed_error_JSX = translateErrorToMessage_JSX(error, prefix_message)

		console.error(function_id + "prefix_message=" + prefix_message
			+ "parsed_error=" + parsed_error,
			"from error=", error)

		set_broadcast_lock(false)

		var new_state = {
			"status": sendtx_status.sentfail,
			"error": parsed_error,
			...specific_state,  // will override if necessary
		}

		// using condition because sometimes this.myrun is null here
		if (this.myrun) {
			new_state["owner"] = this.myrun.owner.address  // used to create link to wallet on explorer in "txstate.owner"
		}

		setTxState(tokenActionID, new_state)

		if (callback_func) {
			callback_func()
		}

		// userContext.syncWallet_nForceRerender() // TODO with callback func ?

		// Rather than throwing it is better to return the function actually !
		// This will avoid issues with: try{ try{}catch{ throw here} }catch{ catched here...}
		// if we throw a throwContextError it will display in a popup so we throw a normal one
		// to ensure the execution of the calling function stops
		// throw new Error(parsed_error)
		//this.throwContextError(parsed_error, function_id)
	}



	async function _calculate_fees_for_onchain_runtx(txhash) {
		const function_id = classname + "calculate_fees_for_onchain_runtx(" + txhash + "):\n"
		const log = getLog(function_id)

		var myrun = await runContext.get_run_asyncf()
		const rawtx = await myrun.blockchain.fetch(txhash)
		var bsvtx = new bsv.Transaction(rawtx)

		log("bsvtx =", bsvtx)
		log("calculating inputs for the sent tx: bsvtx.inputs =", bsvtx.inputs)

		var inputAmount = 0

		for (let i = 0; i < bsvtx.inputs.length; i++) {
			const input = bsvtx.inputs[i]
			if (!input.output) {
				const prevtxid = input.prevTxId.toString('hex')
				const prevraw = await myrun.blockchain.fetch(prevtxid)
				const prevtx = new bsv.Transaction(prevraw)
				// Add the output, which gives us the script and satoshis
				input.output = prevtx.outputs[input.outputIndex]
			}
			inputAmount += input.output.satoshis
		}

		log("inputAmount =", inputAmount, "& outputAmount =", bsvtx.outputAmount)
		const bsvfee = (inputAmount - bsvtx.outputAmount) // worked in run 0.5
		log("calculated bsvfee =", bsvfee)

		return bsvfee
	}


	/**
	 * helper function to give new_location of updated jig
	 * and it will update the tx_state with the txhash, status & timestamp
	 * 
	 * (awaiting can be useful but is not always necessary even if this function is async
	 * because you can call it at the very end of the parent function)
	 * 
	 * callback_func example: ()=>{userContext.syncWallet_nForceRerender()}
	 */
	async function sendtx_deal_with_success(
		// function_id, tokenActionID, currtx_state, new_location, bsvfee=null, specific_state={}
		// // currtx_state shouldn't be necessary with the Ref, 30.12.2021
		function_id, tokenActionID, new_location, bsvfee = null, specific_state = {}, callback_func
	) {
		const log = getLog(function_id)

		const txhash = new_location.split("_")[0]

		if (!bsvfee) {
			log("calculating bsv fees...")
			try {
				bsvfee = await _calculate_fees_for_onchain_runtx(txhash)
				log("bsvfee=", bsvfee)
			}
			catch (error) {
				return deal_with_error_RETURN(
					// function_id, tokenActionID, currtx_state, error,
					function_id, tokenActionID, error,
					"Envoi réussi mais erreur lors du calcul des frais: "
				)
			}
		}

		var new_state = {
			"bsvfee": bsvfee,
			"hash": txhash,
			"status": sendtx_status.sentsuccess,
			...specific_state, // will override if necessary
		}

		setTxState(tokenActionID, new_state)

		// release lock and update status
		log("broadcast success", txhash)
		set_broadcast_lock(false)

		if (callback_func) {
			callback_func()
		}

		// await this.syncWallet_nForceRerender()
	}


	return (
		<TxStateContext.Provider value={state}>
			{props.children}
		</TxStateContext.Provider>
	)
}

export default TxStateContext
